import React from 'react'
import { Col, Row, Button } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { Box, Chip, Paper, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import ActivityDetailsWizard from '../../components/Details/ActivityDetailsWizard'
import ActivityTemplateDropDialog from '../../components/Dialogs/ActivityTemplateDropDialog'
import { activityFromTemplate, formatDuration, recurrentFromTemplate } from '../../components/utils/activityTemplates'
import { imageThumbnailUrl } from '../../components/utils/images'
import { DEFAULT_SPORTS, DEFAULT_TYPES } from '../../sports'
import { isActivityPast, wallClockNow } from '../../components/utils/activityPast'

moment.tz.setDefault('Etc/GMT-3')
const localizer = momentLocalizer(moment)
// Only for dropping templates in from the side panel; the activities themselves are not
// draggable (draggableAccessor below).
const DnDCalendar = withDragAndDrop(Calendar)

const dateNavStyle = { cursor: 'pointer', margin: 10 }

function EmptyToolbar() {
  return <div className='rbc-toolbar'></div>
}

function ActivityEvent({ event }) {
  if (event.preview) {
    return (
      <span>
        <div>{event.title}</div>
        <div style={{ fontSize: '0.75em' }}>Pudota tähän</div>
      </span>
    )
  }
  const participants = event.data?.participants ? event.data.participants.length : 0
  const maxp = event.data?.maxp
  return (
    <span>
      <div>{event.title}</div>
      <div style={{ fontSize: '0.75em', opacity: 0.8 }}>
        {participants}{maxp ? '/' + maxp : ''} ilm.
        {event.data?.sport ? ' | ' + event.data.sport : ''}
        {event.data?.skillLevel ? ' | ' + event.data.skillLevel : ''}
      </div>
    </span>
  )
}

class ActivitiesCalendarContainer extends React.Component {
  constructor(props) {
    super(props)
    this.filterRef = React.createRef()
    this.state = {
      loading: true,
      activities: [],
      events: [],
      selectedWeek: moment.utc().startOf('isoWeek').format('YYYY-MM-DD'),
      activityFilter: 'upcoming',
      sportFilter: null,
      activityDetailsWizardOpen: false,
      selectedActivity: null,
      images: [],
      sports: DEFAULT_SPORTS,
      activitytypes: DEFAULT_TYPES,
      calendarHeight: '75vh',
      focusTab: 0,
      newrecurrent: false,
      // a new activity pre-filled from a selected calendar slot
      slotActivity: null,
      // weekday (1-7) for a recurring series started from a dropped template
      slotWeekday: null,
      // the pre-filled activity came from a template: its entry times follow a moved start
      slotFromTemplate: false,
      // activity templates in the side panel, and one dropped on the calendar waiting for
      // confirmation (docs/features/activity-templates.md)
      templates: [],
      drop: null,
      dropNotice: null,
      // where a template being dragged would land: { start, end, title } as stored strings
      dragPreview: null,
    }
  }

  async componentDidMount() {
    this.unsubscribeTemplates = this.props.firebase.listenActivityTemplates().onSnapshot((snapshot) => {
      const templates = snapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((t) => t.state !== 'deleted')
        .sort((a, b) => (a.templatename || '').localeCompare(b.templatename || ''))
      this.setState({ templates })
    })
    await this.updateData()
    this.props.firebase.listenMedia().onSnapshot((snapshot) => {
      const images = []
      snapshot.forEach((doc) => {
        if (doc.data().mimetype.indexOf('image/') > -1) {
          images.push({ id: doc.id, ...doc.data() })
        }
      })
      this.setState({ images })
    })
    this.props.firebase.getSports().onSnapshot((doc) => {
      const sports = [...DEFAULT_SPORTS]
      if (doc.exists) {
        sports.push(...doc.data().list)
      }
      const uniqueSports = [...new Map(sports.map((item) => [item['key'], item])).values()]
      uniqueSports.sort((a, b) => a.label.localeCompare(b.label))
      this.setState({ sports: uniqueSports })
    })
    this.props.firebase.getActivitytypes().onSnapshot((doc) => {
      const activitytypes = [...DEFAULT_TYPES]
      if (doc.exists) {
        activitytypes.push(...doc.data().list)
      }
      const uniqueActivitytypes = [...new Map(activitytypes.map((item) => [item['key'], item])).values()]
      uniqueActivitytypes.sort((a, b) => a.label.localeCompare(b.label))
      this.setState({ activitytypes: uniqueActivitytypes })
    })
    this.updateCalendarHeight()
  }

  componentWillUnmount() {
    if (this.unsubscribeTemplates) this.unsubscribeTemplates()
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.activities !== this.state.activities ||
      prevState.sportFilter !== this.state.sportFilter ||
      prevState.showPast !== this.state.showPast
    ) {
      this.updateCalendarHeight()
    }
  }

  updateCalendarHeight = () => {
    if (this.filterRef && this.filterRef.current) {
      const filterHeight = this.filterRef.current.clientHeight
      const navbarHeight = 62
      const padding = 30
      const available = window.innerHeight - filterHeight - navbarHeight - padding
      const calendarHeight = Math.max(available, 300)
      this.setState({ calendarHeight: calendarHeight + 'px' })
    }
  }

  updateData = async () => {
    const _activities = await this.props.firebase.getActivities()
    let _d = _activities.docs.map((sh) => {
      return { ...sh.data(), id: sh.id }
    })
    // The wizard renders selectedActivity, not the list, so it has to be re-derived from
    // the fresh documents. Without this every refresh triggered from inside the wizard -
    // check in, move to queue, add payment - refetched the list and left the wizard on the
    // copy it opened with. ActivitiesContainer.filterData already does the same thing.
    const selected = this.state.selectedActivity
    const updatedSelected = selected ? _d.find((r) => r.id === selected.id) : null
    this.setState(
      {
        activities: _d,
        loading: false,
        ...(updatedSelected ? { selectedActivity: updatedSelected } : {}),
      },
      this.filterData
    )
  }

  filterData = () => {
    // UTC wall clock, like the stored times (utils/activityPast.js)
    const now = wallClockNow()
    const events = this.state.activities
      .filter((r) => {
        const isPast = isActivityPast(r, now)
        return this.state.activityFilter === 'past' ? isPast : !isPast
      })
      .filter((r) => {
        if (this.state.sportFilter) {
          return r.sport === this.state.sportFilter
        }
        return true
      })
      .filter((r) => r.state !== 'deleted')
      .map((activity) => ({
        id: activity.id,
        title: activity.name?.fi || '',
        start: moment.utc(activity.start, 'YYYY-MM-DDTHH:mm').toDate(),
        end: moment.utc(activity.end, 'YYYY-MM-DDTHH:mm').toDate(),
        data: activity,
      }))
    this.setState({ events })
  }

  startAccessor = (event) => {
    return moment(event.start).subtract(180, 'minutes').toDate()
  }

  endAccessor = (event) => {
    return moment(event.end).subtract(180, 'minutes').toDate()
  }

  moveWeek = (weeks) => {
    let date
    if (weeks > 0) {
      date = moment.utc(this.state.selectedWeek).add(weeks, 'weeks')
    } else if (weeks < 0) {
      date = moment.utc(this.state.selectedWeek).subtract(-weeks, 'weeks')
    } else {
      date = moment.utc().startOf('isoWeek')
    }
    if (date.isValid()) {
      this.setState({ selectedWeek: date.format('YYYY-MM-DD') })
    }
  }

  addNewActivity = () => {
    this.setState({ selectedActivity: null, newrecurrent: false, slotActivity: null, slotWeekday: null, slotFromTemplate: false }, this.toggleActivityDetailsWizard)
  }

  addNewActivityRecurrent = () => {
    this.setState({ selectedActivity: null, newrecurrent: true, slotActivity: null, slotWeekday: null, slotFromTemplate: false }, this.toggleActivityDetailsWizard)
  }

  // An empty slot (click or drag) starts a new one-off activity at that time. The calendar
  // shows the stored UTC wall clock as is (see startAccessor), so the slot's wall clock in
  // the localizer's zone is the value to store. A plain click selects one 30 min step;
  // make that an hour, like the activity default.
  handleSelectSlot = (slotInfo) => {
    const start = moment(slotInfo.start)
    let end = moment(slotInfo.end)
    if (end.diff(start, 'minutes') < 60) end = start.clone().add(60, 'minutes')
    const slotActivity = {
      start: start.format('YYYY-MM-DDTHH:mm'),
      end: end.format('YYYY-MM-DDTHH:mm'),
    }
    this.setState({ selectedActivity: null, newrecurrent: false, slotActivity, slotWeekday: null, slotFromTemplate: false }, this.toggleActivityDetailsWizard)
  }

  // The calendar shows the stored UTC wall clock as is (see startAccessor), so the slot's wall
  // clock in the localizer's zone is the value to store - the same as handleSelectSlot.
  slotToStored = (date) => moment(date).format('YYYY-MM-DDTHH:mm')

  handleTemplateDragStart = (event, template) => {
    this.draggedTemplate = template
    // some browsers start no drag without data
    event.dataTransfer.setData('text/plain', template.templatename || template.id)
    event.dataTransfer.effectAllowed = 'copy'
  }

  // The slot under the pointer while a template is dragged over the grid. react-big-calendar's
  // own preview (dragFromOutsideItem) fires the drop on every hover in this version, so the
  // preview is worked out here: the day from the column, the time from the gutter label of the
  // row (as displayed) plus 30 min in its lower half - the same slot the drop will report.
  slotAtPoint = (x, y) => {
    const columns = [...document.querySelectorAll('.rbc-time-content .rbc-day-slot')]
    const column = columns.findIndex((col) => {
      const box = col.getBoundingClientRect()
      return x >= box.left && x < box.right
    })
    if (column < 0) return null
    const group = [...document.querySelectorAll('.rbc-time-gutter .rbc-timeslot-group')].find((g) => {
      const box = g.getBoundingClientRect()
      return y >= box.top && y < box.bottom
    })
    if (!group) return null
    const label = (group.textContent || '').trim().replace('.', ':')
    const hour = moment.utc(label, 'HH:mm')
    if (!hour.isValid()) return null
    const box = group.getBoundingClientRect()
    const half = y >= box.top + box.height / 2 ? 30 : 0
    const day = moment.utc(this.state.selectedWeek, 'YYYY-MM-DD').add(column, 'days')
    return day.hour(hour.hour()).minute(hour.minute() + half).format('YYYY-MM-DDTHH:mm')
  }

  handleCalendarDragOver = (event) => {
    event.preventDefault()
    const template = this.draggedTemplate
    if (!template) return
    const start = this.slotAtPoint(event.clientX, event.clientY)
    const current = this.state.dragPreview
    if (!start) {
      if (current) this.setState({ dragPreview: null })
      return
    }
    if (current && current.start === start) return
    const duration = Number(template.duration) > 0 ? Number(template.duration) : 60
    const end = moment.utc(start, 'YYYY-MM-DDTHH:mm').add(duration, 'minutes').format('YYYY-MM-DDTHH:mm')
    this.setState({ dragPreview: { start, end, title: template.templatename || (template.name && template.name.fi) || '' } })
  }

  handleTemplateDragEnd = () => {
    this.draggedTemplate = null
    if (this.state.dragPreview) this.setState({ dragPreview: null })
  }

  // the calendar's events plus the drop preview, in the same shape filterData produces
  calendarEvents = () => {
    const preview = this.state.dragPreview
    if (!preview) return this.state.events
    return [
      ...this.state.events,
      {
        id: 'template-drop-preview',
        preview: true,
        title: preview.title,
        start: moment.utc(preview.start, 'YYYY-MM-DDTHH:mm').toDate(),
        end: moment.utc(preview.end, 'YYYY-MM-DDTHH:mm').toDate(),
      },
    ]
  }

  handleDropFromOutside = ({ start }) => {
    const template = this.draggedTemplate
    this.draggedTemplate = null
    if (this.state.dragPreview) this.setState({ dragPreview: null })
    if (!template || !start) return
    const activity = activityFromTemplate(template, this.slotToStored(start))
    this.setState({ drop: { template, activity }, dropNotice: null })
  }

  handleDropCreated = (id, bookingErrors) => {
    const name = this.state.drop && this.state.drop.activity.name ? this.state.drop.activity.name.fi : ''
    const failed = (bookingErrors || []).map((e) => (e.resource ? e.resource.displayName : '?'))
    this.setState({
      drop: null,
      dropNotice: failed.length
        ? 'Luotu: ' + name + '. Osaa kentistä ei voitu varata: ' + failed.join(', ') + '.'
        : 'Luotu: ' + name + '.',
    })
    this.updateData()
  }

  handleDropEdit = () => {
    const { activity } = this.state.drop
    this.setState(
      { drop: null, selectedActivity: null, newrecurrent: false, slotActivity: activity, slotWeekday: null, slotFromTemplate: true },
      this.toggleActivityDetailsWizard
    )
  }

  // A weekly series starting on the dropped day at the dropped time; the recurring creator
  // asks for the end date (docs/features/activity-templates.md).
  handleDropRecurrent = () => {
    const { template, activity } = this.state.drop
    const [date, time] = activity.start.split('T')
    const series = recurrentFromTemplate(template, { date, time })
    this.setState(
      { drop: null, selectedActivity: null, newrecurrent: true, slotActivity: series.activity, slotWeekday: series.weekday, slotFromTemplate: false },
      this.toggleActivityDetailsWizard
    )
  }

  renderTemplatePanel = () => (
    <Paper variant='outlined' sx={{ p: 1, height: this.state.calendarHeight, overflowY: 'auto' }}>
      <Typography variant='subtitle2'>Pohjat</Typography>
      <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
        Vedä pohja kalenteriin, niin aktiviteetti luodaan siihen aikaan.
      </Typography>
      {this.state.templates.length === 0 ? (
        <Typography variant='body2'>
          Ei pohjia. <Link to='/admin/activities/templates'>Lisää pohja</Link>
        </Typography>
      ) : (
        this.state.templates.map((template) => {
          const src = imageThumbnailUrl(template.media) || template.poster
          return (
            <Box
              key={template.id}
              draggable
              onDragStart={(e) => this.handleTemplateDragStart(e, template)}
              onDragEnd={this.handleTemplateDragEnd}
              title='Vedä kalenteriin'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.75,
                mb: 0.75,
                border: '1px solid #ddd',
                borderRadius: 1,
                backgroundColor: '#fff',
                cursor: 'grab',
                '&:hover': { borderColor: '#1976d2' },
              }}
            >
              {src ? (
                <Box component='img' src={src} alt='' sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 0.5, flex: 'none' }} />
              ) : null}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='body2' noWrap>
                  {template.templatename || (template.name && template.name.fi)}
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap display='block'>
                  {formatDuration(template.duration)}
                  {template.sport ? ' · ' + template.sport : ''}
                  {template.publish ? '' : ' · luonnos'}
                </Typography>
              </Box>
            </Box>
          )
        })
      )}
    </Paper>
  )

  handleSelectEvent = (event) => {
    const activity = this.state.activities.find((a) => a.id === event.id)
    if (activity) {
      this.setState({ selectedActivity: activity }, this.toggleActivityDetailsWizard)
    }
  }

  toggleActivityDetailsWizard = () => {
    const closing = this.state.activityDetailsWizardOpen
    this.setState(
      {
        activityDetailsWizardOpen: !this.state.activityDetailsWizardOpen,
        focusTab: 0,
      },
      closing ? this.updateData : undefined
    )
  }

  eventStyleGetter = (event) => {
    if (event.preview) {
      return {
        style: {
          color: '#0d47a1',
          backgroundColor: 'rgba(25, 118, 210, 0.15)',
          border: '2px dashed #1976d2',
          borderRadius: '4px',
          pointerEvents: 'none',
        },
      }
    }
    let style = {
      color: 'white',
      borderRadius: '4px',
      border: 'none',
    }
    const state = event.data?.state
    if (state === 'published') {
      style.backgroundColor = '#1976d2'
    } else if (state === 'draft') {
      style.backgroundColor = '#9e9e9e'
    } else if (state === 'cancelled') {
      style.backgroundColor = '#d32f2f'
    } else {
      style.backgroundColor = '#1976d2'
    }
    return { style }
  }

  getSportFilters = () => {
    const sportsInUse = [...new Set(this.state.activities.map((a) => a.sport).filter(Boolean))]
    return sportsInUse.sort()
  }

  render() {
    if (this.state.loading) {
      return (
        <div className='content'>
          <Row>
            <Col>Loading ...</Col>
          </Row>
        </div>
      )
    }

    if (this.state.activityDetailsWizardOpen) {
      return (
        <div className='content'>
          <Row>
            <Col>
              <Button onClick={this.toggleActivityDetailsWizard}>Palaa kalenteriin</Button>
            </Col>
          </Row>
          <ActivityDetailsWizard
            sports={this.state.sports}
            activitytypes={this.state.activitytypes}
            activity={this.state.selectedActivity}
            images={this.state.images}
            customerdata={this.props.customerdata}
            profile={this.props.profile}
            user={this.props.user}
            onUpdate={this.updateData}
            recurrent={this.state.newrecurrent}
            initTab={this.state.focusTab}
            initialActivity={this.state.slotActivity}
            initialWeekday={this.state.slotWeekday}
            followStart={this.state.slotFromTemplate}
          />
        </div>
      )
    }

    return (
      <div className='content'>
        <div ref={this.filterRef}>
          <Row className='mb-2'>
            <Col>
              <Button size='sm' onClick={this.addNewActivity}>Lisää tapahtuma/aktiviteetti</Button>{' '}
              <Button size='sm' onClick={this.addNewActivityRecurrent}>Lisää toistuva</Button>
            </Col>
          </Row>
          <Row>
            <Col md='8'>
              <Chip
                label='Tulevat'
                clickable
                size='small'
                color='primary'
                variant={this.state.activityFilter === 'upcoming' ? 'default' : 'outlined'}
                onClick={() => this.setState({ activityFilter: 'upcoming' }, this.filterData)}
                style={{ marginRight: 4 }}
              />
              <Chip
                label='Menneet'
                clickable
                size='small'
                color='primary'
                variant={this.state.activityFilter === 'past' ? 'default' : 'outlined'}
                onClick={() => this.setState({ activityFilter: 'past' }, this.filterData)}
                style={{ marginRight: 4 }}
              />
              {this.getSportFilters().map((sport) => (
                <Chip
                  key={sport}
                  label={sport}
                  clickable
                  size='small'
                  color='info'
                  variant={this.state.sportFilter === sport ? 'default' : 'outlined'}
                  onClick={() =>
                    this.setState(
                      { sportFilter: this.state.sportFilter === sport ? null : sport },
                      this.filterData
                    )
                  }
                  style={{ marginRight: 4, marginBottom: 4 }}
                />
              ))}
              {this.state.sportFilter ? (
                <Chip
                  label='Kaikki lajit'
                  clickable
                  size='small'
                  color='default'
                  variant='outlined'
                  onClick={() => this.setState({ sportFilter: null }, this.filterData)}
                  style={{ marginRight: 4, marginBottom: 4 }}
                />
              ) : null}
            </Col>
          </Row>
          <Row className='mb-2'>
            <Col md='6' className='align-middle'>
              <KeyboardArrowLeftIcon onClick={() => this.moveWeek(-1)} style={dateNavStyle} />
              <strong style={{ textTransform: 'capitalize' }}>
                Viikko {moment.utc(this.state.selectedWeek).format('WW (D.M.YYYY')}
                {' - '}
                {moment.utc(this.state.selectedWeek).endOf('isoWeek').format('D.M.YYYY)')}
              </strong>
              <KeyboardArrowRightIcon onClick={() => this.moveWeek(1)} style={dateNavStyle} />
              <Button size='sm' color='link' onClick={() => this.moveWeek(0)}>
                Tänään
              </Button>
            </Col>
          </Row>
        </div>
        {this.state.dropNotice ? (
          <Row className='mb-2'>
            <Col>
              <Typography variant='body2' sx={{ color: 'success.main' }}>
                {this.state.dropNotice}
              </Typography>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col md='9'>
            <DnDCalendar
              style={{ height: this.state.calendarHeight, backgroundColor: '#FFFFFF' }}
              draggableAccessor={() => false}
              resizable={false}
              onDropFromOutside={this.handleDropFromOutside}
              onDragOver={this.handleCalendarDragOver}
              localizer={localizer}
              events={this.calendarEvents()}
              startAccessor={this.startAccessor}
              endAccessor={this.endAccessor}
              views={['week']}
              defaultView={Views.WEEK}
              step={30}
              min={new Date(1972, 0, 1, 6, 0, 0)}
              max={new Date(1972, 0, 1, 22, 59, 59)}
              date={moment.utc(this.state.selectedWeek, 'YYYY-MM-DD').toDate()}
              onNavigate={() => {}}
              onSelectEvent={this.handleSelectEvent}
              selectable
              onSelectSlot={this.handleSelectSlot}
              components={{
                event: ActivityEvent,
                toolbar: EmptyToolbar,
              }}
              eventPropGetter={this.eventStyleGetter}
            />
          </Col>
          <Col md='3'>{this.renderTemplatePanel()}</Col>
        </Row>
        <ActivityTemplateDropDialog
          open={!!this.state.drop}
          template={this.state.drop && this.state.drop.template}
          activity={this.state.drop && this.state.drop.activity}
          resources={(this.props.customerdata && this.props.customerdata.resources) || []}
          attaching={!!(this.props.customerdata && this.props.customerdata.features && this.props.customerdata.features.activityattaching)}
          onClose={() => this.setState({ drop: null })}
          onCreated={this.handleDropCreated}
          onEdit={this.handleDropEdit}
          onRecurrent={this.handleDropRecurrent}
        />
      </div>
    )
  }
}

export default withFirebase(ActivitiesCalendarContainer)
